import type { ReactElement } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { DashboardSignalPanel } from "@/modules/dashboard/components/redesign/DashboardSignalPanel";
import {
  MoneyValue,
  SIGNAL_UNIT_STYLE,
} from "@/modules/dashboard/components/redesign/MoneyValue";
import type { DashboardLossBreakdownPanelProps } from "@/modules/dashboard/types/dashboard-analytics.types";

/** How many reasons the panel lists before folding the rest into "other". */
const REASON_DISPLAY_LIMIT = 4;

/**
 * Why orders don't become money.
 *
 * One measure (money lost) across nominal reasons, so every bar is the SAME
 * hue at varying length — colouring each reason differently would spend the
 * identity channel re-encoding what bar length already shows.
 */
export function DashboardLossBreakdownPanel({
  loss,
  formatValue,
  formatValueParts,
  reasonLabel,
}: DashboardLossBreakdownPanelProps): ReactElement {
  const t = useTranslations();
  const isEmpty = loss.lostOrders === 0;
  const shown = loss.reasons.slice(0, REASON_DISPLAY_LIMIT);
  // Scale bars against the biggest reason, not the total, so the smaller
  // reasons stay visible instead of collapsing to slivers.
  const widest = Math.max(...shown.map((reason) => reason.amount), 1);

  return (
    <DashboardSignalPanel
      title={t(keys.dashboard.lossTitle)}
      subtitle={t(keys.dashboard.lossSubtitle)}
      isEmpty={isEmpty}
      emptyTitle={t(keys.dashboard.lossEmptyTitle)}
      emptyBody={t(keys.dashboard.lossEmptyBody)}
    >
      <div className="flex items-baseline gap-2">
        <span className="truncate text-[22px] font-bold leading-[1.3] text-[color:var(--text-primary)]">
          {/* Panel headline: the unit sits subordinate to the figure, like the
              headline tiles. The reason rows below stay joined — at 11.5px they
              are body text, where a half-size symbol would be unreadable. */}
          <MoneyValue
            {...formatValueParts(loss.lostAmount)}
            unitStyle={SIGNAL_UNIT_STYLE}
          />
        </span>
        <span className="shrink-0 text-[11.5px] text-[color:var(--text-muted)]">
          {t(keys.dashboard.lossOrdersCount, { count: loss.lostOrders })}
        </span>
      </div>

      <ul className="mt-3 flex flex-col gap-2.5">
        {shown.map((reason) => {
          const label = reasonLabel(reason.code, reason.status);
          const orders = t(keys.dashboard.ordersCount, {
            count: reason.orders,
          });
          // One string for both channels: the mouse gets it as `title`, every
          // other input gets it as the bar's accessible name. A bare `title`
          // is unreachable by keyboard and absent on touch, so it can never be
          // the only carrier of the count — the same count is printed as text
          // under the bar as well.
          const summary = t(keys.dashboard.barSummary, {
            label,
            amount: formatValue(reason.amount),
            orders,
          });
          return (
            <li key={`${reason.status}-${reason.code}`} className="min-w-0">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-[11.5px] text-[color:var(--text-secondary)]">
                  {label}
                </span>
                {/* Money and its order count travel together — a reason that
                    names only a sum doesn't say whether it is one big order or
                    nine small ones, which is the whole decision the seller is
                    trying to make here. */}
                <span className="flex shrink-0 items-baseline gap-1.5">
                  <span className="font-mono text-[11.5px] font-semibold tabular-nums text-[color:var(--text-primary)]">
                    <bdi>{formatValue(reason.amount)}</bdi>
                  </span>
                  <span className="text-[10.5px] text-[color:var(--text-muted)]">
                    <bdi>{orders}</bdi>
                  </span>
                </span>
              </div>
              <div
                role="img"
                aria-label={summary}
                title={summary}
                className="mt-1 h-[5px] w-full overflow-hidden rounded-full bg-[color:var(--track)]"
              >
                <div
                  aria-hidden="true"
                  className="h-full rounded-full bg-[color:var(--error-500)]"
                  style={{
                    width: `${Math.max(2, (reason.amount / widest) * 100)}%`,
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>

      {loss.reasons.length > REASON_DISPLAY_LIMIT ? (
        <p className="mt-2 text-[11px] text-[color:var(--text-muted)]">
          {t(keys.dashboard.lossMoreReasons, {
            count: loss.reasons.length - REASON_DISPLAY_LIMIT,
          })}
        </p>
      ) : null}
    </DashboardSignalPanel>
  );
}
